import "maplibre-gl/dist/maplibre-gl.css";
import { useEffect, useRef } from "react";
import { Map as MapLibreMap } from "maplibre-gl";
import { Button } from "@www/ui/button";
import {
  ChevronLeft,
  Download,
  Filter,
  Layers,
  MapIcon,
  Search,
} from "lucide-react";
import { Card } from "@www/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@www/ui/accordion";
import { ScrollArea } from "@www/ui/scroll-area";
import { withIdentityPoolId } from "@aws/amazon-location-utilities-auth-helper";
import { getPublicConfig } from "~/public-config";
import { getDefaultMapStyle } from "~/utils/map";

const config = getPublicConfig();

export function ClientMapDemo() {
  const mapRef = useRef<MapLibreMap | null>(null);

  useEffect(() => {
    if (mapRef.current) {
      return;
    }

    const initializeMap = async () => {
      const authHelper = await withIdentityPoolId(
        config.aws.identityPool.mapId,
      );

      const map = new MapLibreMap({
        container: "map",
        center: [-82, 28],
        maxBounds: [-100.0, 20.0, -70.0, 40.0],
        zoom: 6,
        minZoom: 4,

        ...authHelper.getMapAuthenticationOptions(),
        style: getDefaultMapStyle().styleUrl,
      });

      mapRef.current = map;
    };

    initializeMap();

    return () => {
      mapRef.current?.remove();
    };
  }, []);

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      <div className="h-full w-full" id="map" />
      <div className="fixed inset-0 z-40 z-50 bg-black/10 backdrop-blur-sm" />

      <MockTopBar />
      <MockPermitList />
    </div>
  );
}

function MockTopBar() {
  return (
    <div className="bg-base-100 absolute left-1/2 top-4 z-10 -translate-x-1/2 rounded-lg px-4 py-2 opacity-90 shadow-lg">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" className="flex items-center gap-2">
          <Layers className="mr-2 h-4 w-4" />
          <span>Layers</span>
        </Button>

        <Button variant="ghost" size="sm" className="flex items-center gap-2">
          <Search className="mr-2 h-4 w-4" />
          <span>Search</span>
        </Button>

        <Button variant="ghost" size="sm" className="flex items-center gap-2">
          <Filter className="mr-2 h-4 w-4" />
          <span>Filters</span>
        </Button>

        <Button variant="ghost" size="sm" className="flex items-center gap-2">
          <Download className="mr-2 h-4 w-4" />
          <span>Download</span>
        </Button>

        <Button variant="ghost" size="sm" className="flex items-center gap-2">
          <MapIcon className="mr-2 h-4 w-4" />
          <span>Styles</span>
        </Button>
      </div>
    </div>
  );
}

const permitTypes = [
  { color: "#ff7f0e", label: "Residential" },
  { color: "#9467bd", label: "Industrial" },
  { color: "#59a14f", label: "Agricultural" },
  { color: "#e15759", label: "Government" },
  { color: "#1f77b4", label: "Commercial" },
  { color: "#9d7660", label: "Existing" },
];

const mockPermits = [
  {
    properties: {
      project_name: "Oak Ridge Residential",
      type: "Residential",
      received_date: "2024-01-15",
      address: "123 Oak Street",
      acreage: "5.2",
    },
  },
  {
    properties: {
      project_name: "Industrial Park Phase 2",
      type: "Industrial",
      received_date: "2024-02-01",
      address: "456 Industry Way",
      acreage: "12.8",
    },
  },
  {
    properties: {
      project_name: "Green Acres Farm",
      type: "Agricultural",
      received_date: "2024-02-15",
      address: "789 Rural Road",
      acreage: "45.3",
    },
  },
];

function MockPermitList() {
  return (
    <Card className="bg-base-100 absolute left-4 top-4 z-10 w-80 opacity-90">
      <div className="border-border/50 flex items-center border-b px-4 py-2">
        <button className="text-foreground hover:text-muted-foreground flex items-center gap-1 text-sm font-bold">
          <ChevronLeft size={16} />
          Go to Dashboard
        </button>
      </div>

      <Accordion type="multiple" defaultValue={["permit-types", "permit-list"]}>
        <AccordionItem value="permit-types" className="border-none">
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <span className="font-semibold">Permit Type Key</span>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-3">
            <ul className="space-y-2 text-sm">
              {permitTypes.map((type) => (
                <li
                  key={type.label}
                  className="flex flex-row items-center text-center"
                >
                  <div
                    className="mr-2 h-3 w-3 rounded-sm"
                    style={{ backgroundColor: type.color }}
                  />
                  <p>{type.label}</p>
                </li>
              ))}
            </ul>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem
          value="permit-list"
          className="border-border/50 border-t"
        >
          <AccordionTrigger className="px-4 py-3 hover:no-underline">
            <span className="font-semibold">
              Permits ({mockPermits.length})
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <ScrollArea className="w-full">
              <div className="space-y-1 p-2">
                {mockPermits.map((permit) => (
                  <div
                    className="m-1 flex cursor-pointer flex-row rounded-lg border p-1 py-2 text-xs shadow-sm hover:opacity-60"
                    key={permit.properties.project_name}
                  >
                    <div>
                      <ul>
                        <li>
                          <b>Project</b>: {permit.properties.project_name}
                        </li>
                        <li>
                          <b>Applicant</b>: {permit.properties.type}
                        </li>
                        <li>
                          <b>Type</b>:
                          {`${permit.properties.type} / ${permit.properties.acreage}`}
                        </li>
                        <li>
                          <b>Issued</b>: {permit.properties.received_date}
                        </li>
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}
